import {computed, inject, Injectable, linkedSignal, resource, signal} from '@angular/core';
import {Observable} from 'rxjs';
import {take, tap} from 'rxjs/operators';
import {cloneDeep as _cloneDeep, isEqual as _isEqual} from 'lodash';
import {debouncedSignal} from '../../utils/signals/debounced-signal';
import {HttpService} from '../technique/http.service';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';
import {PlcProduitComplementaireDTO} from '../../dtos/plc-produit-complementaire-dto';
import {
  ProduitDeclinaisonComplementaireDTO
} from '../../dtos/produit/declinaison/complementaire/produit-declinaison-complementaire-dto';
import {
  PeriodeConditionnementDTO
} from '../../dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {PlcProduitComplementaireChanges} from '../../models/gestion-produits/declinaison/complementaire/plc/plc-produit-complementaire-changes';
import {JourSemaine} from '../../enums/client/jour-semaine.enum';
import {ProduitComplementaireParametrageService} from './produit-complementaire-parametrage.service';
import {GraphQLService} from "../technique/graphql.service";

/** Enregistrement commun aux deux écrans : chaque ligne postée porte son point de livraison. */
export const URL_POST_SAVE_PRODUITS_COMPLEMENTAIRES = `dolrest/produits-declinaisons-complementaires/produit-complementaire/save`;

export const PRODUIT_COMPLEMENTAIRE_PAGE_SIZE = 20;
export const PREMIERE_PAGE = 1;
export const SEARCH_DEBOUNCE_MS = 300;

export const sliceForPage = <T>(fullList: T[], page: number): T[] =>
  fullList.slice((page - 1) * PRODUIT_COMPLEMENTAIRE_PAGE_SIZE, page * PRODUIT_COMPLEMENTAIRE_PAGE_SIZE);

export const buildPageCount = (total: number): number =>
  Math.max(1, Math.ceil(total / PRODUIT_COMPLEMENTAIRE_PAGE_SIZE));

/**
 * Critères déclenchant une requête réseau. `plcId` à `null` : rien à charger. La pagination n'en fait pas partie,
 * elle se fait côté client sur la liste complète.
 *
 * <p>Uniquement des champs primitifs : `_isEqual` (voir `debouncedSignal` plus bas) s'appuie là-dessus pour
 * dédupliquer correctement. Un champ non primitif (Date, tableau/objet recréé...) fausserait la comparaison.</p>
 */
interface FiltresRecherche {
  plcId: number | null;
  libelle: string;
}

const FILTRES_VIDE: FiltresRecherche = {plcId: null, libelle: ''};

@Injectable({
  providedIn: 'root'
})
export class PlcProduitComplementaireService {

  private readonly httpSvc = inject(HttpService);
  private readonly graphQlSvc = inject(GraphQLService);
  private readonly parametrageSvc = inject(ProduitComplementaireParametrageService);

  private readonly plcIdSignal = signal<number | null>(null);

  private readonly gridFiltresSignal = signal<FiltresRecherche>(FILTRES_VIDE);
  private readonly catalogueFiltresSignal = signal<FiltresRecherche>(FILTRES_VIDE);

  private readonly gridFiltresAppelSignal = debouncedSignal(this.gridFiltresSignal, SEARCH_DEBOUNCE_MS, _isEqual);
  private readonly catalogueFiltresAppelSignal = debouncedSignal(this.catalogueFiltresSignal, SEARCH_DEBOUNCE_MS, _isEqual);

  private readonly gridPageAskedSignal = signal<number>(PREMIERE_PAGE);
  private readonly cataloguePageAskedSignal = signal<number>(PREMIERE_PAGE);

  private readonly editedByIdMap = signal<Map<number, PlcProduitComplementaireDTO>>(new Map());
  private readonly deletedByIdMap = signal<Map<number, PlcProduitComplementaireDTO>>(new Map());

  readonly hasChangesSignal = computed(() => !!this.editedByIdMap().size || !!this.deletedByIdMap().size);

  private readonly plcProduitComplementaireResource = resource({
    params: this.gridFiltresAppelSignal,
    defaultValue: [] as PlcProduitComplementaireDTO[],
    loader: async ({params}) => params.plcId == null
      ? []
      : this.fetchParametrageList(params.plcId, params.libelle)
  });

  /** Lignes du serveur amputées des suppressions en attente : c'est ce que la grille pagine. */
  private readonly visibleListSignal = computed<PlcProduitComplementaireDTO[]>(() => {
    const deletedByIdMap = this.deletedByIdMap();
    return (this.plcProduitComplementaireResource.value() ?? [])
      .filter(row => !deletedByIdMap.has(row.produitDeclinaisonId));
  });

  readonly gridTotalSignal = computed(() => this.visibleListSignal().length);

  private readonly gridPageCount = computed(() => buildPageCount(this.gridTotalSignal()));

  /** Une suppression ou une recherche peut vider la dernière page : on ne sort jamais des bornes. */
  readonly gridPageSignal = computed(() => Math.min(this.gridPageAskedSignal(), this.gridPageCount()));

  /** Ligne serveur de la page courante, avant saisies en attente : base de comparaison pour `collectPageEdits`. */
  private readonly pageSnapshotSignal = computed<PlcProduitComplementaireDTO[]>(() =>
    sliceForPage(this.visibleListSignal(), this.gridPageSignal()));

  /**
   * Reste modifiable — la grille écrit dedans — mais repart du serveur dès que la page, la liste ou les saisies
   * en attente changent.
   */
  readonly currentPageList = linkedSignal<{ snapshotList: PlcProduitComplementaireDTO[]; editedByIdMap: Map<number, PlcProduitComplementaireDTO> },
    PlcProduitComplementaireDTO[]>({
    source: () => ({snapshotList: this.pageSnapshotSignal(), editedByIdMap: this.editedByIdMap()}),
    computation: ({snapshotList, editedByIdMap}) =>
      snapshotList.map(row => _cloneDeep(editedByIdMap.get(row.produitDeclinaisonId)) ?? row)
  });

  initPlc = (plcId: number | null): void => {
    this.plcIdSignal.set(plcId);
    this.gridFiltresSignal.set({...FILTRES_VIDE, plcId});
    this.catalogueFiltresSignal.set(FILTRES_VIDE);
    this.gridPageAskedSignal.set(PREMIERE_PAGE);
    // Vide aussi les saisies désormais abandonnées : `currentPageList` repart du serveur.
    this.resetPendingChanges();
  };

  private resetPendingChanges = (): void => {
    this.editedByIdMap.set(new Map());
    this.deletedByIdMap.set(new Map());
  };

  goToPage = (page: number): void => {
    this.collectPageEdits();
    this.gridPageAskedSignal.set(page);
  };

  searchGrid = (libelle: string): void => {
    this.collectPageEdits();
    this.gridPageAskedSignal.set(PREMIERE_PAGE);
    this.gridFiltresSignal.update(filtres => ({...filtres, libelle: libelle.trim()}));
  };

  private fetchParametrageList = async (plcId: number, libelle: string): Promise<PlcProduitComplementaireDTO[]> => {
    const parts: string[] = [`plcId: ${plcId}`];
    if (libelle) {
      parts.push(`libelle: "${this.graphQlSvc.escapeQueryValue(libelle)}"`);
    }

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allPlcProduitComplementaires(filters: { ${parts.join(', ')} }) {
          ${this.parametrageSvc.PLC_PRODUIT_COMPLEMENTAIRE_PARAMETRAGE}
        }
      }
    `).pipe(take(1)).toPromise();

    return ((response?.allPlcProduitComplementaires ?? []) as any[]).map(this.parametrageSvc.mapLigne);
  };

  /** Reporte les saisies de la page courante dans les modifications en attente. */
  collectPageEdits = (): void => {
    const snapshotByIdMap = new Map(this.pageSnapshotSignal().map(row => [row.produitDeclinaisonId, row]));
    const editedByIdMap = new Map(this.editedByIdMap());

    this.currentPageList().forEach(row => {
      const snapshot = snapshotByIdMap.get(row.produitDeclinaisonId);
      if (!snapshot) {
        return;
      }

      if (PlcProduitComplementaireChanges.isSame(snapshot, row)) {
        editedByIdMap.delete(row.produitDeclinaisonId);
        return;
      }
      editedByIdMap.set(row.produitDeclinaisonId, _cloneDeep(row));
    });
    this.editedByIdMap.set(editedByIdMap);
  };

  toggleJourConsommation = (produitDeclinaisonId: number, jourId: JourSemaine): void => {
    this.currentPageList.update(list => this.parametrageSvc.toggleJourConsommation(list, produitDeclinaisonId, jourId));
    this.collectPageEdits();
  };

  applyPeriodeConditionnementList = (produitDeclinaisonId: number, periodeList: PeriodeConditionnementDTO[]): void => {
    this.currentPageList.update(list => list.map(row => row.produitDeclinaisonId !== produitDeclinaisonId
      ? row
      : {...row, periodeConditionnementList: periodeList}));
    this.collectPageEdits();
  };

  /** La ligne disparaît de la liste visible, donc du total et de la page, par le seul jeu des signaux dérivés. */
  markDeleted = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    this.deletedByIdMap.update(current =>
      new Map(current).set(produitComplementaire.produitDeclinaisonId, _cloneDeep(produitComplementaire)));
  };

  /**
   * L'écran ne travaille que sur un point de livraison, mais l'enregistrement regroupe les lignes par point de
   * livraison : chacune part avec le sien, sans dépendre de ce que la grille a reçu du serveur.
   */
  buildChanges = (): PlcProduitComplementaireChanges => {
    this.collectPageEdits();
    const deletedByIdMap = this.deletedByIdMap();
    return PlcProduitComplementaireChanges.buildWithEditAndDeleteList(
      [...this.editedByIdMap().values()].filter(row => !deletedByIdMap.has(row.produitDeclinaisonId))
                                        .map(this.withPlcId),
      [...deletedByIdMap.values()].map(this.withPlcId));
  };

  private withPlcId = (produitComplementaire: PlcProduitComplementaireDTO): PlcProduitComplementaireDTO =>
    ({...produitComplementaire, plcId: this.plcIdSignal()});

  saveChanges = (changes: PlcProduitComplementaireChanges): Observable<ResponseWrapper<PlcProduitComplementaireDTO>> =>
    this.httpSvc.post(URL_POST_SAVE_PRODUITS_COMPLEMENTAIRES, changes)
        .pipe(tap((response: ResponseWrapper<PlcProduitComplementaireDTO>) => {
          this.applySavedRowList(response?.resultList ?? []);
          this.resetPendingChanges();
        }));

  /**
   * Un ajout crée des lignes qui n'existent pas dans la liste en mémoire : seule une relecture peut les faire
   * apparaître au bon rang. `.reload()` reste donc justifié ici, à la différence de `saveChanges` qui ne modifie
   * que des lignes déjà chargées.
   *
   * <p>Le catalogue ignore le point de livraison : c'est celui de l'écran qui est porté sur chaque ligne ajoutée.</p>
   */
  addProduits = (produitToAddList: PlcProduitComplementaireDTO[]): Observable<ResponseWrapper<PlcProduitComplementaireDTO>> =>
    this.httpSvc.post(URL_POST_SAVE_PRODUITS_COMPLEMENTAIRES,
                      PlcProduitComplementaireChanges.buildWithProduitToAddList(produitToAddList.map(this.withPlcId)))
        .pipe(tap(() => {
          this.collectPageEdits();
          this.plcProduitComplementaireResource.reload();
        }));

  /**
   * Aligne la liste serveur sur ce que la sauvegarde a produit : les lignes supprimées la quittent, les lignes
   * acceptées y sont fusionnées. Sans ça, vider les modifications en attente ferait réapparaître les suppressions
   * et repasserait la page aux valeurs d'avant sauvegarde.
   *
   * <p>La réponse porte les lignes acceptées, pas une relecture de la base : c'est la fusion qui les normalise
   * comme la base les stockera.</p>
   */
  private applySavedRowList = (savedList: PlcProduitComplementaireDTO[]): void => {
    const deletedByIdMap = this.deletedByIdMap();
    const savedByIdMap = new Map(savedList.map(row => [row.produitDeclinaisonId, row]));

    this.plcProduitComplementaireResource.update(list => (list ?? [])
      .filter(row => !deletedByIdMap.has(row.produitDeclinaisonId))
      .map(row => {
        const saved = savedByIdMap.get(row.produitDeclinaisonId);
        return saved ? PlcProduitComplementaireChanges.mergeParametrage(row, _cloneDeep(saved)) : row;
      }));
  };

  private readonly catalogueResource = resource({
    params: this.catalogueFiltresAppelSignal,
    defaultValue: [] as ProduitDeclinaisonComplementaireDTO[],
    loader: async ({params}) => params.plcId == null
      ? []
      : this.fetchCatalogueList(params.plcId, params.libelle)
  });

  readonly catalogueTotalSignal = computed(() => (this.catalogueResource.value() ?? []).length);

  private readonly cataloguePageCount = computed(() => buildPageCount(this.catalogueTotalSignal()));

  readonly cataloguePageSignal = computed(() => Math.min(this.cataloguePageAskedSignal(), this.cataloguePageCount()));

  readonly catalogueList = computed<ProduitDeclinaisonComplementaireDTO[]>(() =>
    sliceForPage(this.catalogueResource.value() ?? [], this.cataloguePageSignal()));

  private fetchCatalogueList = async (plcId: number, libelle: string): Promise<ProduitDeclinaisonComplementaireDTO[]> => {
    const parts: string[] = [`plcId: ${plcId}`];
    if (libelle) {
      parts.push(`libelle: "${this.graphQlSvc.escapeQueryValue(libelle)}"`);
    }

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allDeclinaisonsComplementaires(filters: { ${parts.join(', ')} }) {
          id,
          libelle,
          code,
          declinaison {
            id,
            libelle,
            code
          },
          fabrique,
          uniteDeMesure {
            id,
            libelle,
            abreviation
          },
          mcpConditionnementVarianteList {
            id,
            libelle
          }
        }
      }
    `).pipe(take(1)).toPromise();

    return (response?.allDeclinaisonsComplementaires ?? []) as ProduitDeclinaisonComplementaireDTO[];
  };

  searchCatalogue = (libelle: string): void => {
    this.cataloguePageAskedSignal.set(PREMIERE_PAGE);
    this.catalogueFiltresSignal.update(filtres => ({...filtres, libelle: libelle.trim()}));
  };

  goToCataloguePage = (page: number): void => this.cataloguePageAskedSignal.set(page);

  /** Ouverture du dialogue : filtres remis à zéro, donc rechargement — le catalogue a pu changer entre-temps. */
  openCatalogue = (): void => {
    this.cataloguePageAskedSignal.set(PREMIERE_PAGE);
    this.catalogueFiltresSignal.set({...FILTRES_VIDE, plcId: this.plcIdSignal()});
  };

  /** Fermeture : plus de PLC dans les filtres, donc plus rien de chargé. */
  closeCatalogue = (): void => this.catalogueFiltresSignal.set(FILTRES_VIDE);
}
